import { ACTIVE_LOCATION_STORAGE_KEY, OFFLINE_HEADERS } from '@jq4race/shared';

import { ApiError, type AuthErrorCode } from './errors.js';
import { handleLocationAccessDenied } from './location-access-interceptor.js';

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
  // When true, the client will retry once after a 401 by hitting /auth/refresh.
  // Default true. Set to false for the refresh call itself (avoid loops).
  refreshOn401?: boolean;
  /**
   * pwa-offline-first Slice 6 (foreground drainer) — extra headers merged on
   * top of the defaults. Used to carry the offline-replay headers
   * (X-Captured-At/X-Client-Sent-At) AND, critically, `X-Location-Id` fixed
   * to the item's OWN captured location (second adversarial review finding
   * #2 — the drainer runs in the background and the operator may have since
   * switched locations; the ambient `localStorage`-derived value must never
   * silently win over a caller's own explicit choice). Header NAMES are
   * matched case-insensitively against `content-type`/`authorization`/
   * `cookie`/`x-location-id` (finding #8) — a caller can never override the
   * auth/cookie headers this function sets itself, in any casing.
   */
  headers?: Record<string, string>;
  /**
   * pwa-offline-first Slice 6, second adversarial review finding #4 — a 403
   * response normally triggers `handleLocationAccessDenied()` (clears the
   * ambient active-location + dispatches a re-pick event) UNCONDITIONALLY.
   * That side effect is only safe for a FOREGROUND request the operator is
   * actively waiting on — a BACKGROUND sync (the drainer) must never yank
   * the operator's current, unrelated foreground work out from under them
   * just because one queued item's OWN (possibly stale/reassigned) location
   * came back denied. Set `true` for any drainer-originated request.
   */
  suppressLocationAccessHandling?: boolean;
}

const PROTECTED_HEADER_NAMES = new Set(['content-type', 'authorization', 'cookie']);

/**
 * Merges caller-supplied headers underneath this function's own
 * content-type/authorization — case-insensitively, so a caller can never
 * sneak a differently-cased `Authorization`/`Cookie`/`Content-Type` key past
 * the protection (second adversarial review finding #8).
 */
function buildHeaders(callerHeaders: Record<string, string> | undefined): Record<string, string> {
  const headers: Record<string, string> = {};
  for (const [key, value] of Object.entries(callerHeaders ?? {})) {
    if (PROTECTED_HEADER_NAMES.has(key.toLowerCase())) continue;
    headers[key] = value;
  }
  headers['content-type'] = 'application/json';
  return headers;
}

/** Case-insensitive "is this header name already present" check. */
function hasHeader(headers: Record<string, string>, name: string): boolean {
  const lower = name.toLowerCase();
  return Object.keys(headers).some((k) => k.toLowerCase() === lower);
}

/** Case-insensitive lookup of the ACTUAL key as the caller cased it, or undefined. */
function findHeaderKey(headers: Record<string, string>, name: string): string | undefined {
  const lower = name.toLowerCase();
  return Object.keys(headers).find((k) => k.toLowerCase() === lower);
}

export type AccessTokenProvider = () => string | null;
export type RefreshFn = () => Promise<string | null>;
export type SessionExpiredHandler = () => void;

let baseUrl = 'http://localhost:3000';
// Base URL for /auth/* endpoints. Kept separate from `baseUrl` so the
// httpOnly `jq4_refresh` cookie can be made first-party in production: when
// frontend and backend live on cross-site domains (e.g. *.up.railway.app, a
// public suffix), the browser drops the third-party refresh cookie and every
// reload/bootstrap loses the session. Pointing auth calls at a same-origin
// path (proxied to the backend) makes the cookie first-party. `null` → fall
// back to `baseUrl` (default; unchanged behavior for existing callers).
let authBaseUrl: string | null = null;
let getAccessToken: AccessTokenProvider = () => null;
let triggerRefresh: RefreshFn = async () => null;
let onSessionExpired: SessionExpiredHandler = () => {};

export function configureClient(opts: {
  baseUrl?: string;
  authBaseUrl?: string;
  getAccessToken: AccessTokenProvider;
  refresh: RefreshFn;
  onSessionExpired: SessionExpiredHandler;
}): void {
  if (opts.baseUrl) baseUrl = opts.baseUrl;
  // '' is a valid value (same-origin); absence resets to null (fall back to
  // baseUrl) so each configureClient call is fully deterministic.
  authBaseUrl = opts.authBaseUrl ?? null;
  getAccessToken = opts.getAccessToken;
  triggerRefresh = opts.refresh;
  onSessionExpired = opts.onSessionExpired;
}

/** Returns the currently configured base URL (useful for low-level fetch helpers). */
export function getBaseUrl(): string {
  return baseUrl;
}

/** Returns the currently configured access token (useful for low-level fetch helpers). */
export function getCurrentAccessToken(): string | null {
  return getAccessToken();
}

// ---------------------------------------------------------------------------
// X-Location-Id header injection
// ---------------------------------------------------------------------------

/**
 * Paths that must NOT receive the X-Location-Id header. These endpoints use
 * :locationId as a path param (assignment management) or return location lists
 * (me/locations) — injecting the header there is meaningless and would confuse
 * any future guard.
 */
const SKIP_LOCATION_HEADER_PATTERNS: RegExp[] = [
  /^\/admin\/locations\/[^/]+\/assignments(\/.*)?$/,
  /^\/admin\/users\/[^/]+\/locations$/,
  /^\/me\/locations$/,
];

/** @internal — exported for contract tests only */
export function shouldInjectLocationHeader(path: string): boolean {
  return !SKIP_LOCATION_HEADER_PATTERNS.some((re) => re.test(path));
}

export async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  // Caller-supplied headers are merged first, with content-type/authorization
  // (case-insensitively) stripped from them — this function is always the
  // sole author of those two, so a caller can never override the auth header
  // this way, in any casing (second adversarial review finding #8).
  const headers = buildHeaders(opts.headers);
  const token = getAccessToken();
  if (token) headers.authorization = `Bearer ${token}`;

  // /auth/* endpoints go through authBaseUrl when configured so the refresh
  // cookie stays first-party; everything else hits the data API base. The
  // trailing slash keeps the match tight (won't catch a future /authors etc.).
  const effectiveBase = path.startsWith('/auth/') && authBaseUrl !== null ? authBaseUrl : baseUrl;

  // Inject active location header for operational endpoints (FR-20) — but
  // ONLY when the caller did not already supply one (second adversarial
  // review finding #2: a background drainer sync must send the ITEM's own
  // captured `X-Location-Id`, which must win over whatever the operator's
  // CURRENT foreground location happens to be in localStorage).
  if (
    shouldInjectLocationHeader(path) &&
    !hasHeader(headers, 'x-location-id') &&
    typeof localStorage !== 'undefined'
  ) {
    const activeLocationId = localStorage.getItem(ACTIVE_LOCATION_STORAGE_KEY);
    if (activeLocationId) {
      headers['X-Location-Id'] = activeLocationId;
    }
  }

  const init: RequestInit = {
    method: opts.method ?? 'GET',
    headers,
    credentials: 'include',
    signal: opts.signal,
  };
  if (opts.body !== undefined) init.body = JSON.stringify(opts.body);

  let res: Response;
  try {
    res = await fetch(`${effectiveBase}${path}`, init);
  } catch (err) {
    throw new ApiError('network_error', err instanceof Error ? err.message : 'fetch failed');
  }

  if (res.status === 401 && opts.refreshOn401 !== false && path !== '/auth/refresh') {
    const newToken = await triggerRefresh().catch(() => null);
    if (newToken) {
      // pwa-offline-first Slice 7 (deferred finding from Slice 6 round 5,
      // LOW #5) — a caller carrying the offline replay headers (the sync
      // drainer, via checkin-create.handler.ts) computes X-Client-Sent-At
      // ONCE, right before its own call. If THIS attempt 401s, the refresh
      // round-trip above can take a meaningful amount of wall-clock time —
      // reusing that now-stale value on the retry would corrupt the
      // server's clock-skew correction (design D15: `corrected = capturedAt
      // + (serverNow - clientSentAt)`). Recompute it fresh for the retry
      // only; X-Captured-At (the actual capture instant) is untouched.
      let retryHeaders = opts.headers;
      if (retryHeaders) {
        const clientSentAtKey = findHeaderKey(retryHeaders, OFFLINE_HEADERS.clientSentAt);
        if (clientSentAtKey) {
          retryHeaders = { ...retryHeaders, [clientSentAtKey]: new Date().toISOString() };
        }
      }
      return request<T>(path, { ...opts, headers: retryHeaders, refreshOn401: false });
    }
    onSessionExpired();
  }

  if (res.status === 204) return undefined as T;

  let payload: unknown = null;
  try {
    payload = await res.json();
  } catch {
    payload = null;
  }

  if (!res.ok) {
    const serverCode = (payload as { code?: AuthErrorCode } | null)?.code;
    const code = serverCode ?? mapStatusToCode(res.status);
    const message = (payload as { message?: string } | null)?.message ?? `HTTP ${res.status}`;

    // 403 with location-denial codes: clear localStorage + dispatch event
    // (FR-17, WU-24) — SUPPRESSED for background-drain requests (second
    // adversarial review finding #4): the drainer must never yank the
    // operator's current foreground location out from under them just
    // because one queued item's own (possibly stale) location was denied.
    if (res.status === 403 && !opts.suppressLocationAccessHandling) {
      handleLocationAccessDenied(String(code));
    }

    throw new ApiError(code, message, res.status, payload, serverCode === undefined);
  }

  return payload as T;
}

function mapStatusToCode(status: number): AuthErrorCode {
  if (status === 401) return 'invalid_credentials';
  if (status === 409) return 'refresh_in_progress';
  if (status === 423) return 'account_locked';
  if (status === 429) return 'rate_limited';
  return 'unknown';
}
